/* eslint-disable max-lines */
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { mergeStyles } from '@cian/utils/lib/shared/style';
import { TextField } from '@cian/text-field-component';
import { Popup } from '@cian/popup-component';
import { OutsideClick } from '@cian/outside-click-component';

import { getKeyboardEventCode, Preloader } from '@cian/components';

import { scrollTo } from '@cian/components/dist/utils';

import * as textSearch from './utils/text_search';

const style = require('./suggest.css');

export interface ISuggestItem<T> {
  title: string;
  value: T;
  subTitle?: string;
  externalLink?: string;
}

export interface ISuggestGroup<T> {
  title: string;
  items: ISuggestItem<T>[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AbstractSuggestItem = ISuggestItem<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AbstractSuggestGroup = ISuggestGroup<any>;

type AbstractSuggestions = AbstractSuggestItem[] | AbstractSuggestGroup[];

const isISuggestGroup = (suggestions: AbstractSuggestions): suggestions is AbstractSuggestGroup[] =>
  'items' in suggestions[0];

export interface ISuggestProps {
  placeholder?: string;
  suggestions?: AbstractSuggestions | null;
  loading?: boolean;
  disabled?: boolean;

  onFocus?(): void;
  onBlur?(): void;
  onValueChange(value: string): void;
  onSelect(suggestion: AbstractSuggestItem): void;
  onLinkClick?(suggestion: AbstractSuggestItem): void;

  className?: string;
}

export interface ISuggestState {
  value: string;
  isOpened: boolean;
  selectedItemIndex: number;
}

export class Suggest extends React.Component<ISuggestProps, ISuggestState> {
  public static defaultProps = {
    loading: false,
    disabled: false,
  };

  public state = {
    value: '',
    isOpened: false,
    selectedItemIndex: 0,
  };

  private textField: TextField | null = null;
  private popupElement: HTMLDivElement | null = null;
  private suggestionElements: HTMLLIElement[] | null[] = [];
  private suggestionLastIndex = 0;
  private mouseOverDisabled = false;
  private mouseOverDisableTimeout: number;

  public reset() {
    this.setState({
      value: '',
    });
  }

  public setValue(value: string) {
    this.handleValueChange(value);
  }

  public render() {
    return (
      <OutsideClick
        onOutsideClick={this.handleBlur}
        rootElement={
          <div
            {...mergeStyles(
              style['container'],
              this.state.isOpened && style['container--open'],
              this.props.loading && style['container--loading'],
              this.props.className,
            )}
          />
        }
      >
        <TextField
          ref={textField => (this.textField = textField)}
          placeholder={this.props.placeholder}
          disabled={this.props.disabled}
          inputStyle={style.input}
          value={this.state.value}
          onValueChange={this.handleValueChange}
          onFocus={this.handleFocus}
          onKeyDown={this.handleKeyDown}
        />
        {this.renderPreloader()}
        {this.renderPopup()}
      </OutsideClick>
    );
  }

  private renderPreloader() {
    if (!this.props.loading) {
      return null;
    }

    return (
      <div className={style.preloader}>
        <Preloader size={14} />
      </div>
    );
  }

  private renderPopup() {
    if (!this.state.isOpened) {
      return null;
    }

    if (!this.state.value || !this.props.suggestions) {
      return null;
    }

    return (
      <Popup
        ref={popup => {
          if (popup) {
            // eslint-disable-next-line react/no-find-dom-node
            this.popupElement = ReactDOM.findDOMNode(popup) as HTMLDivElement;
          }
        }}
        containerStyle={style.popup}
      >
        {this.renderList()}
      </Popup>
    );
  }

  private renderList() {
    if (!this.props.suggestions) {
      return null;
    }

    if (this.props.suggestions.length === 0) {
      return <div className={style['nothing-found']}>Ничего не найдено</div>;
    }

    this.suggestionLastIndex = 0;
    this.suggestionElements = [];

    let list: JSX.Element | (JSX.Element | null)[];
    if (isISuggestGroup(this.props.suggestions)) {
      list = this.props.suggestions.map(group => {
        if (group.items.length === 0) {
          return null;
        }

        return (
          <figure key={`suggestion_group-${group.title}`} className={style['list-group']}>
            <figcaption className={style['list-groupTitle']}>{group.title}</figcaption>
            <ul className={style.list}>{this.renderItems(group.items, true)}</ul>
          </figure>
        );
      });
    } else {
      list = <ul className={style.list}>{this.renderItems(this.props.suggestions)}</ul>;
    }

    return <div className={style['list-container']}>{list}</div>;
  }

  private renderItems(items: AbstractSuggestItem[], nested: boolean = false) {
    return items.map(item => {
      const index = this.suggestionLastIndex++;

      return (
        <li
          key={`suggestion-${item.title}`}
          ref={suggestionElement => (this.suggestionElements[index] = suggestionElement)}
          {...mergeStyles(
            style['list-item'],
            nested && style['list-item--nested'],
            item.externalLink && style['list-item--linked'],
            this.state.selectedItemIndex === index && style['list-item--selected'],
          )}
        >
          <a
            className={style['list-itemContainer']}
            onClick={() => this.handleItemClick(item)}
            onMouseOver={() => this.handleItemOver(index)}
            title={item.title}
          >
            {this.renderTitle(item.title)}
            {this.renderSubtitle(item.subTitle)}
          </a>
          {this.renderLink(item)}
        </li>
      );
    });
  }

  private renderTitle(title: string) {
    const value = this.state.value.trim();

    if (!value) {
      return <span>{title}</span>;
    }

    const startIndex = textSearch.indexOf(title, value);
    if (startIndex === -1) {
      return <span>{title}</span>;
    }

    const endIndex = startIndex + value.length;

    return (
      <span>
        {title.substring(0, startIndex)}
        <strong>{title.substring(startIndex, endIndex)}</strong>
        {title.substring(endIndex)}
      </span>
    );
  }

  private renderSubtitle = (subtitle: string | undefined) => {
    if (!subtitle) {
      return undefined;
    }

    return <p className={style['list-itemDesc']}>{subtitle}</p>;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private renderLink(item: ISuggestItem<any>) {
    if (!item.externalLink) {
      return undefined;
    }

    const { onLinkClick } = this.props;

    return (
      <a
        className={style['list-itemLink']}
        href={item.externalLink}
        target="_blank"
        onClick={() => onLinkClick && onLinkClick(item)}
      />
    );
  }

  private handleFocus = () => {
    if (this.props.onFocus) {
      this.props.onFocus();
    }

    this.open();
  };

  private handleBlur = () => {
    if (this.props.onBlur) {
      this.props.onBlur();
    }

    this.close();
  };

  private handleValueChange = (value: string) => {
    this.setState({
      value,
      selectedItemIndex: 0,
    });

    //this.scrollTo(0);

    this.props.onValueChange(value);
  };

  private handleKeyDown = (event: React.KeyboardEvent<EventTarget>) => {
    if (!this.props.suggestions || this.props.suggestions.length === 0) {
      return;
    }

    const nativeEvent = event.nativeEvent as KeyboardEvent;
    const code = getKeyboardEventCode(nativeEvent);

    switch (code) {
      case 'ArrowDown':
      case 'Down':
        event.preventDefault();
        this.selectItem(this.state.selectedItemIndex + 1);
        break;

      case 'ArrowUp':
      case 'Up':
        event.preventDefault();
        this.selectItem(this.state.selectedItemIndex - 1);
        break;

      case 'Tab':
      case 'NumpadEnter':
      case 'Enter':
        event.preventDefault();
        this.handleEnterPress();
        break;
      case 'Escape':
        this.close();
        break;
      default:
        break;
    }
  };

  private handleEnterPress() {
    if (!this.props.suggestions) {
      return;
    }

    let index = this.state.selectedItemIndex;
    if (isISuggestGroup(this.props.suggestions)) {
      for (let i = 0; i <= this.props.suggestions.length - 1; i++) {
        const suggestionsGroup = this.props.suggestions[i];
        if (suggestionsGroup.items.length - 1 < index) {
          index -= suggestionsGroup.items.length;
          continue;
        }

        this.handleItemClick(suggestionsGroup.items[index]);
        break;
      }
    } else {
      this.handleItemClick(this.props.suggestions[index]);
    }
  }

  private handleItemOver = (index: number) => {
    if (this.mouseOverDisabled) {
      return;
    }

    this.setState({
      selectedItemIndex: index,
    });
  };

  private handleItemClick = (item: AbstractSuggestItem) => {
    this.setState({ value: item.title });
    this.props.onSelect(item);
    this.close();
  };

  private selectItem = (index: number) => {
    let innerIndex = index;

    if (this.suggestionLastIndex === 0) {
      return;
    }

    if (innerIndex < 0) {
      innerIndex = this.suggestionLastIndex - 1;
    } else if (index > this.suggestionLastIndex - 1) {
      innerIndex = 0;
    }

    if (this.state.selectedItemIndex === innerIndex) {
      return;
    }

    this.setState({
      selectedItemIndex: innerIndex,
    });

    this.scrollTo(innerIndex);
  };

  private open() {
    if (this.textField) {
      this.textField.focus();
      this.setState({ isOpened: true });
    }
  }

  private close() {
    if (this.textField) {
      this.textField.blur();

      this.setState({
        isOpened: false,
        selectedItemIndex: 0,
      });
    }
  }

  private scrollTo(index: number, center: boolean = false) {
    if (this.popupElement && this.suggestionElements) {
      const containerElement = this.popupElement;
      const valueElement = this.suggestionElements[index];

      if (!valueElement) {
        return;
      }

      window.clearTimeout(this.mouseOverDisableTimeout);
      this.mouseOverDisabled = true;

      scrollTo({ center, containerElement, targetElement: valueElement });

      this.mouseOverDisableTimeout = window.setTimeout(() => {
        this.mouseOverDisabled = false;
      }, 100);
    }
  }
}
